import { Controller } from "@hotwired/stimulus"
import consumer from "../channels/consumer"

/**
 * AI Assistant FAB Controller
 *
 * Manages the AI Assistant floating action button and chat interface.
 * Handles ActionCable subscriptions for real-time AI responses.
 *
 * Usage:
 *   <div data-controller="ai-assistant"
 *        data-ai-assistant-context-type-value="property"
 *        data-ai-assistant-context-id-value="123"
 *        data-ai-assistant-context-label-value="42 Oak Street">
 *
 * The controller initializes Alpine.js data and manages the ActionCable
 * subscription lifecycle for Turbo compatibility.
 */
export default class extends Controller {
  static values = {
    contextType: { type: String, default: "general" },
    contextId: { type: String, default: "" },
    contextLabel: { type: String, default: "" },
    userId: { type: Number, default: 0 }
  }

  connect() {
    // Initialize Alpine data on the element if not already present
    if (!this.element._x_dataStack) {
      this.initializeAlpineData()
    }

    // Subscribe to AI Assistant channel
    this.subscribeToChannel()
  }

  disconnect() {
    // Clean up ActionCable subscription (Turbo-safe)
    this.subscription?.unsubscribe()
  }

  subscribeToChannel() {
    this.subscription = consumer.subscriptions.create(
      {
        channel: "AiAssistantChannel",
        context_type: this.contextTypeValue,
        context_id: this.contextIdValue
      },
      {
        connected: () => this.handleConnected(),
        disconnected: () => this.handleDisconnected(),
        received: (data) => this.handleReceived(data)
      }
    )
  }

  handleConnected() {
    // Dispatch event for Alpine to handle
    this.dispatch("connected")
  }

  handleDisconnected() {
    this.dispatch("disconnected")
  }

  handleReceived(data) {
    // Handle different message types from the AI
    switch (data.type) {
      case "thinking":
        this.dispatch("thinking", { detail: data })
        break
      case "token":
        // Streaming token for typing effect
        this.dispatch("token", { detail: data })
        break
      case "complete":
        // Full response complete
        this.dispatch("complete", { detail: data })
        break
      case "error":
        this.dispatch("error", { detail: data })
        break
      default:
        this.dispatch("message", { detail: data })
    }
  }

  // Send a message to the AI via ActionCable
  // Optional context override allows Alpine to pass the dynamic slideover context
  // instead of the static Stimulus values (which may be for the page, not the entity).
  sendMessage(content, context = null, conversationId = null) {
    if (!this.subscription || !content.trim()) return

    this.subscription.perform("message", {
      content: content,
      context_type: context?.type || this.contextTypeValue,
      context_id: context?.id || this.contextIdValue,
      conversation_id: conversationId
    })
  }

  initializeAlpineData() {
    // Set context from Stimulus values for Alpine to use
    this.element.dataset.context = JSON.stringify({
      type: this.contextTypeValue,
      id: this.contextIdValue,
      label: this.contextLabelValue
    })
  }
}
